import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  createThreeDSecureSession,
  useDuffelCardFormActions,
  type CreateCardForTemporaryUseData,
  type CreateCardForTemporaryUseError,
} from '@duffel/components'

interface PendingAuthorization {
  resolve: (threeDSecureSessionId: string) => void
  reject: (error: Error) => void
}

export interface UseDuffelCardFlowOptions {
  /** Client key minted by the backend. `null` while still loading. */
  clientKey: string | null
  /** Duffel offer ID the 3DS session is bound to. */
  offerId: string
  /**
   * Services being purchased alongside the offer (bags, seats, etc.).
   * Empty for VOY-396 Phase 2 — ancillaries land with VOY-347. Duffel
   * requires the full list here so the total amount to authorise is
   * accurate, so any future additions must be wired through this prop.
   */
  services?: Array<{ id: string; quantity: number }>
}

export interface UseDuffelCardFlowResult {
  /**
   * Props to spread onto `<DuffelCardForm>`. `ref` must be passed
   * separately via the component's `ref` prop — React does not allow refs
   * to be spread from a props object.
   */
  cardFormRef: ReturnType<typeof useDuffelCardFormActions>['ref']
  cardFormProps: {
    clientKey: string
    intent: 'to-create-card-for-temporary-use'
    onValidateSuccess: () => void
    onValidateFailure: () => void
    onCreateCardForTemporaryUseSuccess: (data: CreateCardForTemporaryUseData) => void
    onCreateCardForTemporaryUseFailure: (error: CreateCardForTemporaryUseError) => void
  }
  /**
   * Trigger the full tokenize → 3DS flow. Resolves with a
   * `three_d_secure_session_id` whose status is `ready_for_payment`,
   * ready to be sent to the backend instant booking endpoint. Rejects on
   * validation, tokenization, or 3DS failures so the caller can surface
   * the specific error to the user and retry.
   */
  authorizePayment: () => Promise<string>
  isProcessing: boolean
  isFormValid: boolean
}

/**
 * Encapsulates the Duffel direct card payment flow for a single offer.
 *
 * The consumer renders `<DuffelCardForm ref={cardFormRef} {...cardFormProps} />`
 * anywhere in the form. Calling `authorizePayment()` imperatively drives
 * the card iframe to tokenise, then resolves a 3DS session, and returns
 * the id the backend needs to create an order with `type: 'card'`.
 *
 * Promise-based rather than callback-based so the caller can await it
 * inside its own submit handler and sequence it naturally with the
 * Supabase order insert and the backend `/instant` POST that follows.
 */
export function useDuffelCardFlow({
  clientKey,
  offerId,
  services = [],
}: UseDuffelCardFlowOptions): UseDuffelCardFlowResult {
  const { ref, createCardForTemporaryUse } = useDuffelCardFormActions()
  const pendingRef = useRef<PendingAuthorization | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  // Store the latest clientKey / offerId / services in refs so the
  // callbacks passed into DuffelCardForm always observe the current
  // values even if DuffelCardForm's internal iframe plumbing caches the
  // first handler it receives. Refs are synchronised in an effect (not
  // during render) so we respect the "do not write ref.current during
  // rendering" rule from the React docs.
  const clientKeyRef = useRef(clientKey)
  const offerIdRef = useRef(offerId)
  const servicesRef = useRef(services)
  useEffect(() => {
    clientKeyRef.current = clientKey
    offerIdRef.current = offerId
    servicesRef.current = services
  }, [clientKey, offerId, services])

  // Reject any in-flight authorisation on unmount. Without this, a user
  // who navigates away mid-3DS would leave `pendingRef` dangling — the
  // Duffel iframe callbacks would still fire and write state on an
  // unmounted component. Explicit cleanup also gives the UI a clear
  // "Payment was cancelled" message if the caller is still awaiting.
  useEffect(() => {
    return () => {
      const pending = pendingRef.current
      if (pending) {
        pendingRef.current = null
        pending.reject(new Error('Payment was cancelled.'))
      }
    }
  }, [])

  const settlePending = useCallback(
    (result: { ok: true; value: string } | { ok: false; error: Error }) => {
      const pending = pendingRef.current
      pendingRef.current = null
      setIsProcessing(false)
      if (!pending) return
      if (result.ok) {
        pending.resolve(result.value)
      } else {
        pending.reject(result.error)
      }
    },
    [],
  )

  const handleCardSuccess = useCallback(
    async (data: CreateCardForTemporaryUseData) => {
      const liveClientKey = clientKeyRef.current
      const liveOfferId = offerIdRef.current
      if (!liveClientKey || !liveOfferId) {
        settlePending({ ok: false, error: new Error('Payment is not ready. Please reload and try again.') })
        return
      }
      try {
        const session = await createThreeDSecureSession(
          liveClientKey,
          data.id,
          liveOfferId,
          servicesRef.current,
          true,
        )
        if (session.status !== 'ready_for_payment') {
          // `@duffel/components` opens its own modal for the
          // `client_action_required` challenge. If the promise resolves
          // with anything other than `ready_for_payment` the cardholder
          // either failed the challenge, the session expired, or the
          // card itself is unusable — all map to "retry with a new card".
          settlePending({
            ok: false,
            error: new Error(
              `3D Secure ${session.status}. Please try again or use a different card.`,
            ),
          })
          return
        }
        settlePending({ ok: true, value: session.id })
      } catch (err) {
        settlePending({
          ok: false,
          error: err instanceof Error ? err : new Error(String(err)),
        })
      }
    },
    [settlePending],
  )

  const handleCardFailure = useCallback(
    (err: CreateCardForTemporaryUseError) => {
      settlePending({
        ok: false,
        error: new Error(err?.message || 'Card tokenization failed'),
      })
    },
    [settlePending],
  )

  const authorizePayment = useCallback((): Promise<string> => {
    if (!clientKeyRef.current) {
      return Promise.reject(new Error('Payment form is still initializing. Please wait a moment.'))
    }
    if (!offerIdRef.current) {
      return Promise.reject(new Error('Selected flight offer is no longer available.'))
    }
    if (!isFormValid) {
      return Promise.reject(new Error('Please complete all card details before paying.'))
    }
    if (pendingRef.current) {
      return Promise.reject(new Error('A payment is already in progress.'))
    }
    return new Promise<string>((resolve, reject) => {
      pendingRef.current = { resolve, reject }
      setIsProcessing(true)
      createCardForTemporaryUse()
    })
  }, [isFormValid, createCardForTemporaryUse])

  const cardFormProps = useMemo(
    () => ({
      clientKey: clientKey ?? '',
      intent: 'to-create-card-for-temporary-use' as const,
      onValidateSuccess: () => setIsFormValid(true),
      onValidateFailure: () => setIsFormValid(false),
      onCreateCardForTemporaryUseSuccess: handleCardSuccess,
      onCreateCardForTemporaryUseFailure: handleCardFailure,
    }),
    [clientKey, handleCardSuccess, handleCardFailure],
  )

  return {
    cardFormRef: ref,
    cardFormProps,
    authorizePayment,
    isProcessing,
    isFormValid,
  }
}
